import React, { useCallback, useEffect, useState } from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Container,
  Card,
  CardHeader,
  FormControlLabel,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
  CardContent,
} from "@material-ui/core";

// Karsten
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import useLogs from "hooks/logs";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  captchaOption: {
    fontSize: "1rem",
  },
});

const RecaptchaOptions = ({ IdMarca, Marca, ReCaptchaValidation }) => {
  const classes = { ...useStyles(), ...useCustomStyles() };
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [selectedOption, setSelectedOption] = useState(ReCaptchaValidation);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = useCallback((event) => {
    setSelectedOption(event.target.value);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    await Api.postParams(
      "set-brand-recaptcha-validation-vtex",
      "adminportal",
      "?idMarca=" + IdMarca + "&status=" + selectedOption,
      true
    )
      .then((res) => {
        if (res.Success) {
          setLocalStorageActions({
            nomeTela: "Gerenciamento Recaptcha",
            idMenu: "123",
            acao: `Clicou para atualizar marca ${Marca}`,
          });

          snackbar.showMessage(
            `Configurações do reCAPTCHA VTex do sistema ${Marca} atualizadas com sucesso!`,
            {
              severity: "success",
            }
          );
        }
      })
      .catch((error) => {
        console.log(error);
        snackbar.showMessage(
          "Ocorreu um erro ao registrar problema. Verifique os parâmetros informados.",
          {
            severity: "error",
          }
        );
        setLocalStorageApiProblem({
          idMenuAccess: "123",
          screen: "Gerenciamento reCAPTCHA",
          problem: `Erro na chamada do endpoint set-brand-recaptcha-validation-vtex - ${error}`,
        });
      })
      .finally(() => setSubmitting(false));
  }, [IdMarca, Marca, selectedOption, snackbar]);

  return (
    <Grid item xs={12}>
      <RadioGroup
        aria-label="configuração do reca ptcha para o site"
        name="recaptchaConf"
        defaultValue={"never"}
        value={selectedOption}
        onChange={handleChange}
        className={classes.mb0}
        disabled={submitting}
      >
        <FormControlLabel
          control={<Radio size="small" color="primary" />}
          label="Nunca • nenhuma compra é validada com reCAPTCHA"
          value="never"
          labelPlacement="end"
          classes={{
            root: classes.formControlCheckboxLabelRoot,
            label:
              classes.formControlCheckboxLabelLabel +
              " " +
              classes.captchaOption,
          }}
        />
        <FormControlLabel
          control={<Radio size="small" color="primary" />}
          label="Sempre • cada compra é validada com reCAPTCHA"
          value="always"
          labelPlacement="end"
          classes={{
            root: classes.formControlCheckboxLabelRoot,
            label:
              classes.formControlCheckboxLabelLabel +
              " " +
              classes.captchaOption,
          }}
        />
        <FormControlLabel
          control={<Radio size="small" color="primary" />}
          label="Critério VTEX • Apenas algumas compras são validadas com o reCAPTCHA para minimizar o atrito e melhorar a experiência de compra. O algoritmo da VTEX determina quais sessões são confiáveis e quais devem ser validadas com o reCAPTCHA. Esta é a opção recomendada."
          value="vtexCriteria"
          labelPlacement="end"
          classes={{
            root: classes.formControlCheckboxLabelRoot,
            label:
              classes.formControlCheckboxLabelLabel +
              " " +
              classes.captchaOption,
          }}
        />
      </RadioGroup>
      <Button
        variant="contained"
        type="submit"
        disabled={submitting}
        onClick={handleSubmit}
        style={{ marginBottom: "1.5rem" }}
      >
        {submitting ? "Atualizando..." : "Atualizar"}
      </Button>
    </Grid>
  );
};

function GerenciamentoRecaptcha() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();
  const [brands, setBrands] = useState(null);

  const isMobile = IsMobile();

  const fetchList = async () => {
    return await Api.get(
      "list-brand-recaptcha-validation-vtex",
      "adminportal",
      "",
      true
    )
      .then(({ Success, Result }) => {
        if (Success) {
          setBrands(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "123",
          screen: "Gerenciamento reCAPTCHA",
          problem: `Erro na chamada do list-brand-recaptcha-validation-vtex - ${error}`,
        });
      });
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes
        section="Gerenciamento de reCAPTCHA"
        subsection="Gerenciamento"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container spacing={2} style={{ justifyContent: "center" }}>
          {brands ? (
            brands?.map((brand) => (
              <Grid key={brand.IdMarca} item xs={isMobile ? 12 : 6}>
                <Card classes={{ root: classes.cardRoot }}>
                  <CardHeader
                    subheader={
                      <Grid
                        container
                        component={Box}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Grid item xs="auto">
                          <Box
                            component={Typography}
                            variant="h2"
                            marginBottom="0!important"
                          >
                            <Box component="span">{brand.Marca}</Box>
                          </Box>
                        </Grid>
                      </Grid>
                    }
                    classes={{ root: classes.cardHeaderRoot }}
                  />
                  <CardContent>
                    <RecaptchaOptions {...brand} />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Card classes={{ root: classes.cardRoot }}>
                <CardHeader
                  subheader={
                    <Grid
                      container
                      component={Box}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs="auto">
                        <Box
                          component={Typography}
                          variant="h2"
                          marginBottom="0!important"
                        >
                          <Box component="span">
                            Buscando dados de Gerenciamento de reCAPTCHA...
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  }
                  classes={{ root: classes.cardHeaderRoot }}
                />
                <Grid item xs={12}>
                  <LinearProgress />
                </Grid>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
}

GerenciamentoRecaptcha.propTypes = {};

export default GerenciamentoRecaptcha;
